import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface ReconciliationEntry {
  id: string;
  date: string;
  narration: string;
  voucherType: string;
  deposit: number;
  withdrawal: number;
  amount: number;
  cleared: boolean;
  clearedOn: string | null;
}

export interface ReconciliationView {
  account: { id: string; code: string; name: string };
  asOfDate: string;
  entries: ReconciliationEntry[];
  unclearedCount: number;
  clearedCount: number;
  ledgerBalance: number;
  clearedBalance: number;
  statementBalance: number | null;
  /** Zero means reconciled. Null when no statement balance has been entered. */
  difference: number | null;
  unclearedTotal: number;
}

export function useReconciliation(params: {
  accountCode?: string;
  asOfDate?: string;
  statementBalance?: string;
}) {
  const { accountCode, asOfDate, statementBalance } = params;
  return useQuery({
    queryKey: ['accounts', 'reconciliation', { accountCode, asOfDate, statementBalance }],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (accountCode) q.append('accountCode', accountCode);
      if (asOfDate) q.append('asOfDate', asOfDate);
      if (statementBalance !== undefined && statementBalance !== '') {
        q.append('statementBalance', statementBalance);
      }
      const { data } = await api.get<ReconciliationView>(`/api/accounts/bank/reconciliation?${q.toString()}`);
      return data;
    },
    enabled: !!accountCode,
  });
}

export function useToggleCleared() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { ledgerEntryId: string; cleared: boolean; clearedOn?: string }) =>
      (await api.post('/api/accounts/bank/reconciliation/toggle', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts', 'reconciliation'] }),
  });
}
